import React from 'react';
import HelpTrigger from '../../../help/components/HelpTrigger';
import type { OutreachType } from '../../../types/outreach';
import { outreachTypeIcon } from '../outreachTypeIcons';

interface OutreachTemplate {
  id: string;
  name: string;
  description: string | null;
  type: OutreachType;
  is_system: boolean;
  definition_json: any;
}

interface Props {
  templates: OutreachTemplate[];
  selectedTemplate: OutreachTemplate | null;
  onSelectTemplate: (template: OutreachTemplate | null) => void;
  onStartFromScratch: (type: OutreachType) => void;
}

const getTypeIcon = (type: string) => {
  const Icon = outreachTypeIcon(type);
  return <Icon className="w-5 h-5" />;
};

// The from-scratch kinds — Step 0 is the single "what are you creating" moment
const SCRATCH_KINDS: { value: OutreachType; label: string; description: string }[] = [
  { value: 'campaign', label: 'Campaign', description: 'Collect constituent positions on legislation' },
  { value: 'survey', label: 'Survey', description: 'Detailed feedback with multiple questions' },
  { value: 'poll', label: 'Poll', description: 'Quick single-question feedback' },
  { value: 'petition', label: 'Petition', description: 'A statement people sign; signature count displays publicly' },
  { value: 'message', label: 'Message', description: 'Share information — no questions asked' },
];

const WizardStep0TemplateSelection: React.FC<Props> = ({
  templates,
  selectedTemplate,
  onSelectTemplate,
  onStartFromScratch
}) => {
  const systemTemplates = templates.filter(t => t.is_system);
  const orgTemplates = templates.filter(t => !t.is_system);

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-text-primary dark:text-text-dark-primary mb-2 flex items-center justify-center gap-2">
          What are you creating?
          <HelpTrigger contentKey="outreach-wizard-start" size="sm" />
        </h2>
        <p className="text-text-secondary dark:text-text-dark-secondary">
          Choose a template to get started quickly, or pick a kind to build from scratch.
        </p>
      </div>

      {/* Organization Templates */}
      {orgTemplates.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-text-secondary dark:text-text-dark-secondary mb-3 uppercase tracking-wide">
            Organization Templates
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {orgTemplates.map((template) => (
              <button
                key={template.id}
                onClick={() => onSelectTemplate(template)}
                className={`p-4 border rounded-lg transition-all text-left ${
                  selectedTemplate?.id === template.id
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/10'
                    : 'border-border-light dark:border-border-dark hover:border-primary-300 dark:hover:border-primary-600'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-background-tertiary dark:bg-background-dark-tertiary flex items-center justify-center text-text-primary dark:text-text-dark-primary">
                    {getTypeIcon(template.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-text-primary dark:text-text-dark-primary mb-1">
                      {template.name}
                    </div>
                    {template.description && (
                      <div className="text-sm text-text-secondary dark:text-text-dark-secondary line-clamp-2">
                        {template.description}
                      </div>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* System Templates */}
      {systemTemplates.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-text-secondary dark:text-text-dark-secondary mb-3 uppercase tracking-wide">
            Capitol Commons Templates
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {systemTemplates.map((template) => (
              <button
                key={template.id}
                onClick={() => onSelectTemplate(template)}
                className={`p-4 border rounded-lg transition-all text-left ${
                  selectedTemplate?.id === template.id
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/10'
                    : 'border-border-light dark:border-border-dark hover:border-primary-300 dark:hover:border-primary-600'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/20 flex items-center justify-center text-primary-600 dark:text-primary-400">
                    {getTypeIcon(template.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-text-primary dark:text-text-dark-primary mb-1">
                      {template.name}
                    </div>
                    {template.description && (
                      <div className="text-sm text-text-secondary dark:text-text-dark-secondary line-clamp-2">
                        {template.description}
                      </div>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Start from Scratch — pick a kind (this sets the outreach type) */}
      <div>
        <h3 className="text-sm font-semibold text-text-secondary dark:text-text-dark-secondary mb-3 uppercase tracking-wide">
          Start from Scratch
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {SCRATCH_KINDS.map((kind) => (
            <button
              key={kind.value}
              onClick={() => onStartFromScratch(kind.value)}
              className="p-4 border-2 border-dashed border-border-light dark:border-border-dark rounded-lg hover:border-primary-300 dark:hover:border-primary-600 transition-colors text-left"
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-background-tertiary dark:bg-background-dark-tertiary flex items-center justify-center text-text-secondary dark:text-text-dark-secondary">
                  {getTypeIcon(kind.value)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-text-primary dark:text-text-dark-primary mb-0.5">
                    {kind.label}
                  </div>
                  <div className="text-xs text-text-secondary dark:text-text-dark-secondary">
                    {kind.description}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default WizardStep0TemplateSelection;

